import {
  DetailCard,
  DropdownInput,
  EditableValue,
  Tags,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { useId } from "react";
import {
  CorrectableField,
  FaxDenialReason,
  FaxLetterCategory,
} from "../../../api/postCall/faxLetter";
import "../../authorisationDetails/authorisationDetails.scss";
import SectionApprovalActions from "../../authorisationDetails/components/SectionApprovalActions";
import {
  FEEDBACK_MODE_BADGE_LABEL,
  PLACEHOLDER,
  VALUE_CHANGED_BG,
  VALUE_CHANGED_LABEL,
  VALUE_CHANGED_TEXT,
  type ApprovalStatus,
} from "../../authorisationDetails/utils/constants";
import { AuthorizationValues } from "../hooks/useFaxLetterReview";
import { AUTHORIZATION_TITLE, SECTION_HEADER_CLASS } from "../utils/constants";
import { DENIAL_REASON_OPTIONS } from "../utils/denialReasons";
import {
  CATEGORY_OPTIONS,
  getAuthorizationFields,
  getCategoryTag,
  getStatusLabel,
} from "../utils/faxLetterCategory";
import { fieldClassName } from "./LabelledValue";

interface AuthorizationCardProps {
  /** Current values (the letter as read plus any edits). */
  values: AuthorizationValues;
  /** Fields whose value differs from the letter as read. */
  changedFields: CorrectableField[];
  decision: ApprovalStatus;
  onDecisionChange: (decision: ApprovalStatus) => void;
  onFieldChange: (field: CorrectableField, value: string) => void;
  additionalComments?: string;
  onAdditionalCommentsChange?: (value: string) => void;
  validationErrors?: Partial<Record<CorrectableField, string>>;
}

const ValueChangedTag = () => (
  <Tags
    label={VALUE_CHANGED_LABEL}
    backgroundColor={VALUE_CHANGED_BG}
    textColor={VALUE_CHANGED_TEXT}
  />
);

/**
 * The letter's determination for the J-code, laid out for its category, with Approve / Reject.
 *
 * Mirrors the Auth on File Authorisation Details sections: Approve shows a "Valid" badge; Reject
 * shows "Feedback Mode - Fix Incorrect Fields" and makes every field (and the status) editable; once
 * a value is changed, the field and the section heading show "Value Changed".
 */
const AuthorizationCard = ({
  values,
  changedFields,
  decision,
  onDecisionChange,
  onFieldChange,
  additionalComments = "",
  onAdditionalCommentsChange,
  validationErrors = {},
}: AuthorizationCardProps) => {
  const inputId = useId();
  const category = values.category as FaxLetterCategory;
  const tag = getCategoryTag(category);
  const isEditing = decision === "invalid";
  const changed = new Set(changedFields);

  return (
    <DetailCard
      title={AUTHORIZATION_TITLE}
      headerClassName={SECTION_HEADER_CLASS}
      actions={
        <SectionApprovalActions
          approvalStatus={decision}
          onApprovalStatusChange={onDecisionChange}
          variant="chips"
        />
      }
      badge={
        decision ? (
          changed.size > 0 ? (
            <ValueChangedTag />
          ) : (
            <SectionApprovalActions
              approvalStatus={decision}
              onApprovalStatusChange={onDecisionChange}
              variant="badge"
              invalidBadgeLabel={FEEDBACK_MODE_BADGE_LABEL}
            />
          )
        ) : undefined
      }
    >
      <div
        className={`authorisation-details__row authorisation-details__row--wide !w-full ${
          category === FaxLetterCategory.Denial
            ? "authorisation-details__row--denial"
            : ""
        }`}
      >
        <div className="authorisation-details__field max-w-full">
          <div className="authorisation-details__label flex flex-wrap items-center gap-1.5 font-semibold">
            {getStatusLabel(category)}
            {changed.has("category") && <ValueChangedTag />}
          </div>
          <Tags
            className="authorisation-details__approval-tag"
            label={tag.label}
            backgroundColor={tag.backgroundColor}
            textColor={tag.textColor}
            showBorder={isEditing}
            shouldUsePortal
            {...(isEditing
              ? {
                  options: CATEGORY_OPTIONS,
                  onSelect: (option) => onFieldChange("category", option.value),
                }
              : {})}
          />
        </div>
        {getAuthorizationFields(category)
          .filter(
            ({ key }) =>
              key !== "denial_reason" ||
              values.denial_reason_category === FaxDenialReason.Other,
          )
          .map(({ key, label, fullWidth }) => (
            <div
              key={key}
              className={`${fieldClassName(fullWidth)} max-w-full`}
            >
              <div className="authorisation-details__label flex flex-wrap items-center gap-1.5 font-semibold">
                {key === "denial_reason_category" ? (
                  <span>{label}</span>
                ) : (
                  <label htmlFor={`${inputId}-${key}`}>{label}</label>
                )}
                {changed.has(key) && <ValueChangedTag />}
              </div>
              {key === "denial_reason_category" ? (
                <DropdownInput
                  id={`${inputId}-${key}`}
                  label={label}
                  options={DENIAL_REASON_OPTIONS}
                  value={values.denial_reason_category || FaxDenialReason.Other}
                  onChange={(value) => onFieldChange(key, value)}
                  disabled={!isEditing}
                  size="small"
                  shouldUsePortal
                  className="authorisation-details__denial-reason-dropdown w-full"
                />
              ) : key === "denial_reason" ? (
                <input
                  id={`${inputId}-${key}`}
                  value={values.denial_reason}
                  onChange={(event) => onFieldChange(key, event.target.value)}
                  readOnly={!isEditing}
                  required
                  placeholder="Enter the specific denial reason"
                  className="w-full rounded border border-primaryGray-15 bg-white px-3 py-2 text-[0.6875rem] leading-[1.125rem] focus:outline-none focus:ring-1 focus:ring-primaryGray-7"
                />
              ) : (
                <EditableValue
                  value={values[key] || PLACEHOLDER}
                  onSave={(value) =>
                    onFieldChange(key, value === PLACEHOLDER ? "" : value)
                  }
                  placeholder={label}
                  showEditButton={isEditing}
                  className="authorisation-details__value text-[0.6875rem] leading-[1.125rem]"
                />
              )}
              {validationErrors[key] && (
                <p role="alert" className="mt-1 text-xs text-red-700">
                  {validationErrors[key]}. Choose Reject to correct this field.
                </p>
              )}
            </div>
          ))}
        {category === FaxLetterCategory.Denial && (
          <div className={`${fieldClassName(true)} max-w-full`}>
            <label
              htmlFor={`${inputId}-comments`}
              className="authorisation-details__label gap-1 font-semibold"
            >
              Additional Comments{" "}
              <span className="font-normal text-primaryGray-7">(optional)</span>
            </label>
            <input
              id={`${inputId}-comments`}
              value={additionalComments}
              onChange={(event) =>
                onAdditionalCommentsChange?.(event.target.value)
              }
              placeholder="Add any further context or details"
              className="w-full rounded border border-primaryGray-15 bg-white px-3 py-2 text-[0.6875rem] leading-[1.125rem] focus:outline-none focus:ring-1 focus:ring-primaryGray-7"
            />
          </div>
        )}
      </div>
    </DetailCard>
  );
};

export default AuthorizationCard;
